import { useState, useEffect, useRef } from 'react';
import PageHero from '../components/PageHero.jsx';
import RequirementCheck from '../components/RequirementCheck.jsx';
import CoursePicker from '../components/CoursePicker.jsx';
import InfoIcon from '../components/InfoIcon.jsx';
import Pill from '../components/Pill.jsx';
import { courses } from '../data/courses.js';
import { bellSchedule } from '../data/bellSchedule.js';
import {
  getSchedule,
  setSchedule,
  clearSchedule,
  DEFAULT_SCHEDULE,
} from '../lib/storage.js';
import './ScheduleBuilder.css';

// ── Static data ──────────────────────────────────────────────────────────────

const courseById = Object.fromEntries(courses.map(c => [c.id, c]));

const CORE_SLOTS = [
  {
    defaultId: 'ela-6',
    advancedId: 'ela-6-advanced',
    swapToAdvancedLabel: 'Want ELA 6 Advanced?',
    swapToStandardLabel: 'Switch back to ELA 6',
    prereqWarning: null,
  },
  {
    defaultId: 'math-6',
    advancedId: 'math-6-7',
    swapToAdvancedLabel: 'Want Math 6/7 (Advanced Pathway)?',
    swapToStandardLabel: 'Switch back to Math 6',
    prereqWarning:
      'Math 6/7 requires Math 5/6 completion or qualification (communicated to all 5th grade math students). Confirm with your counselor.',
  },
  { defaultId: 'science-6',        advancedId: null },
  { defaultId: 'social-studies-6', advancedId: null },
];

const DANCE_COVER_OPTION = {
  id: 'dance-cover-pe',
  name: "I'll cover this with Dance",
  code: null,
  description:
    'Pick Dance MS 1 in Fine Arts — it satisfies both fine arts and physical activity.',
  source: null,
};

const PE_OPTIONS = [
  ...courses.filter(c => Number(c.grade) === 6 && c.category === 'pe'),
  DANCE_COVER_OPTION,
];

const FINE_ARTS_OPTIONS = courses
  .filter(c => Number(c.grade) === 6 && c.category === 'fine-arts')
  .map(c =>
    c.id === 'dance-ms-1'
      ? { ...c, note: 'Counts as both fine arts AND physical activity.' }
      : c
  );

const ELECTIVE_OPTIONS = courses.filter(
  c => Number(c.grade) === 6 && c.category === 'elective'
);

// ── Bell-schedule helpers (for "See my day" view) ────────────────────────────

function defaultScheduleId() {
  const d = new Date().getDay();
  return (d >= 2 && d <= 4) ? 'tues-wed-thurs' : 'monday-friday';
}

function ampm(t) {
  const h = parseInt(t.split(':')[0], 10);
  return `${t} ${(h >= 8 && h <= 11) ? 'AM' : 'PM'}`;
}

function durationMins(start, end) {
  const toM = s => { const [h, m] = s.split(':').map(Number); return h * 60 + m; };
  return toM(end) - toM(start);
}

// ── Generic helpers ───────────────────────────────────────────────────────────

function formatDate(isoString) {
  if (!isoString) return 'Not yet saved';
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
}

function relativeTime(isoString) {
  if (!isoString) return null;
  const secs = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (secs < 10) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min${mins !== 1 ? 's' : ''} ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs} hr${hrs !== 1 ? 's' : ''} ago`;
}

// ── CoreCard (inline — only this page) ───────────────────────────────────────

function CoreCard({ courseId, onSwap, swapTarget, swapLabel, prereqWarning }) {
  const course = courseById[courseId];
  if (!course) return null;
  return (
    <div className={`sb-core-card${course.advanced ? ' sb-core-card--advanced' : ''}`}>
      <div className="sb-core-card-header">
        <div className="sb-core-card-name-row">
          <span className="sb-core-card-name">{course.name}</span>
          {course.code && <span className="sb-core-card-code">{course.code}</span>}
          <InfoIcon source={course.source} />
        </div>
        {course.advanced && <Pill variant="advanced" size="sm">Advanced</Pill>}
      </div>
      <p className="sb-core-card-desc">{course.description}</p>
      {prereqWarning && (
        <div className="sb-prereq-warning">
          <span aria-hidden="true">⚠</span>
          <span>{prereqWarning}</span>
        </div>
      )}
      {swapTarget && (
        <button type="button" className="sb-swap-link" onClick={() => onSwap(swapTarget)}>
          {swapLabel} →
        </button>
      )}
    </div>
  );
}

// ── DayView (inline — only this page) ────────────────────────────────────────

function DayView({ core, pe, fineArts, electives }) {
  const [scheduleId, setScheduleId] = useState(defaultScheduleId);

  const schedule = bellSchedule.schedules.find(s => s.id === scheduleId);
  const periods = [
    ...schedule.common,
    ...schedule.byGrade['6'],
    ...schedule.tail,
  ];

  const daySource = {
    ...bellSchedule.source,
    note: bellSchedule.verificationNote,
    verificationNeeded: true,
  };

  const realPe = pe && pe !== 'dance-cover-pe';
  const selectedCourses = [
    ...core.map(id => courseById[id]).filter(Boolean),
    realPe ? courseById[pe] : null,
    ...fineArts.map(id => courseById[id]).filter(Boolean),
    ...electives.map(id => courseById[id]).filter(Boolean),
  ].filter(Boolean);

  return (
    <div className="sb-day-view">
      <div className="sb-day-disclaimer">
        <strong>Period assignments aren't known yet.</strong> The school builds the master
        schedule over the summer and assigns each class to a specific period. You'll see
        your actual schedule in Home Access Center before August 12. This view shows
        <em> when</em> each period happens so you can picture the shape of your day.
      </div>

      <div className="sb-day-toggle-row">
        <div className="bell-toggle" role="group" aria-label="Select day type">
          {bellSchedule.schedules.map(s => (
            <button
              key={s.id}
              type="button"
              className={`bell-toggle-btn${scheduleId === s.id ? ' bell-toggle-btn--active' : ''}`}
              onClick={() => setScheduleId(s.id)}
            >
              {s.id === 'monday-friday' ? 'Mon / Fri' : 'Tue / Wed / Thu'}
            </button>
          ))}
          <InfoIcon source={daySource} />
        </div>
      </div>

      <div className="sb-period-card">
        <table className="sb-period-table">
          <thead>
            <tr>
              <th className="sb-period-th">Period</th>
              <th className="sb-period-th">Time</th>
              <th className="sb-period-th sb-period-th--len">Length</th>
            </tr>
          </thead>
          <tbody>
            {periods.map((row, i) => {
              const mins = durationMins(row.start, row.end);
              const barW = Math.min(100, Math.round((mins / 60) * 100));
              return (
                <tr
                  key={i}
                  className={[
                    'sb-period-row',
                    row.isLunch ? 'sb-period-row--lunch' : '',
                    row.period === 'Advisory' ? 'sb-period-row--advisory' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <td className="sb-period-label">{row.period}</td>
                  <td className="sb-period-time">{ampm(row.start)} – {ampm(row.end)}</td>
                  <td className="sb-period-dur">
                    <span className="sb-dur-text">{mins} min</span>
                    <span
                      className="sb-dur-bar"
                      style={{ '--bar-w': `${barW}%` }}
                      aria-hidden="true"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="sb-day-courses">
        <h3 className="sb-day-courses-title">Your courses — period order TBA</h3>
        <p className="sb-day-courses-note">
          The school assigns each course to a period over the summer. You'll see the
          actual assignment in Home Access Center before the first day.
        </p>
        {selectedCourses.length > 0 ? (
          <ul className="sb-day-course-list">
            {selectedCourses.map(c => (
              <li key={c.id} className="sb-day-course-item">
                <span className="sb-day-course-name">{c.name}</span>
                {c.code && <span className="sb-day-course-code">{c.code}</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="sb-day-no-courses">
            No courses selected yet — switch to the Schedule tab and build your schedule.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Page component ────────────────────────────────────────────────────────────

export default function ScheduleBuilder() {
  const [initSchedule] = useState(() => getSchedule());

  const [core, setCore] = useState(() => {
    const c = initSchedule.selections?.core;
    return Array.isArray(c) && c.length === 4
      ? c
      : [...DEFAULT_SCHEDULE.selections.core];
  });
  const [pe, setPe] = useState(() => initSchedule.selections?.pe ?? null);
  const [fineArts, setFineArts] = useState(() => {
    const fa = initSchedule.selections?.fineArts;
    return Array.isArray(fa) ? fa : (typeof fa === 'string' && fa ? [fa] : []);
  });
  const [electives, setElectives] = useState(() => {
    const e = initSchedule.selections?.electives;
    return Array.isArray(e) ? e : [];
  });
  const [lastSaved, setLastSaved] = useState(
    () => initSchedule.updatedAt ?? null
  );
  const [view, setView] = useState('schedule');
  const [copied, setCopied] = useState(false);
  const [clearConfirm, setClearConfirm] = useState(false);
  const [shareTooltipVisible, setShareTooltipVisible] = useState(false);

  const isFirstRender = useRef(true);
  const copyTimer = useRef(null);
  const shareTimer = useRef(null);
  const shareDismissTimer = useRef(null);

  // Tick every 15s to refresh relative-time display
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 15000);
    return () => clearInterval(id);
  }, []);

  // Save to localStorage on every selection change, skip first render
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const now = new Date().toISOString();
    setSchedule({
      version: 1,
      updatedAt: now,
      grade: 6,
      selections: { core, pe, fineArts, electives },
    });
    setLastSaved(now);
  }, [core, pe, fineArts, electives]);

  // Mobile: show "Share" tooltip once after 3s, dismiss after 5s
  useEffect(() => {
    const isTouch = window.matchMedia('(hover: none)').matches;
    if (!isTouch) return;
    try {
      if (sessionStorage.getItem('crms.seenShareTooltip')) return;
    } catch { return; }

    shareTimer.current = setTimeout(() => {
      setShareTooltipVisible(true);
      try { sessionStorage.setItem('crms.seenShareTooltip', '1'); } catch {}
      shareDismissTimer.current = setTimeout(
        () => setShareTooltipVisible(false),
        5000
      );
    }, 3000);

    return () => {
      clearTimeout(shareTimer.current);
      clearTimeout(shareDismissTimer.current);
    };
  }, []);

  // ── Computed requirements ────────────────────────────────────────────────

  const danceIsFineArt = fineArts.includes('dance-ms-1');
  const realPeSelected = pe !== null && pe !== 'dance-cover-pe';
  const physActivityOk = realPeSelected || danceIsFineArt;
  const fineArtsOk     = fineArts.length >= 1;
  const peCount        = realPeSelected ? 1 : 0;
  const fineArtsCount  = fineArts.length;
  const totalCourses   = 4 + peCount + fineArtsCount + electives.length;
  const totalOk        = totalCourses >= 6 && totalCourses <= 8;

  // Dimmed IDs for mutual Dance/PE awareness
  const dimmedPeIds = danceIsFineArt
    ? PE_OPTIONS.map(o => o.id)
    : [];
  const dimmedFaIds = realPeSelected ? ['dance-ms-1'] : [];

  // ── Handlers ────────────────────────────────────────────────────────────

  function handleCoreSwap(index, newId) {
    setCore(prev => {
      const next = [...prev];
      next[index] = newId;
      return next;
    });
  }

  // Auto-clear PE when Dance MS 1 is added to fine arts
  function handleFineArtsChange(newArr) {
    const danceJustAdded = newArr.includes('dance-ms-1') && !fineArts.includes('dance-ms-1');
    setFineArts(newArr);
    if (danceJustAdded && pe !== null) {
      setPe(null);
    }
  }

  function generatePlainText() {
    const coreCourses     = core.map(id => courseById[id]).filter(Boolean);
    const peCourse        = realPeSelected ? courseById[pe] : null;
    const faCourses       = fineArts.map(id => courseById[id]).filter(Boolean);
    const electiveCourses = electives.map(id => courseById[id]).filter(Boolean);

    const peText = peCourse
      ? `  ${peCourse.name} (${peCourse.code})`
      : danceIsFineArt
        ? '  Dance MS 1 — see Fine Arts'
        : '  (none selected)';

    return [
      'My CRMS Schedule — 6th Grade',
      `Last updated: ${formatDate(lastSaved)}`,
      '',
      'Core (required):',
      '',
      ...coreCourses.map(c => `  ${c.name} (${c.code || 'no code'})`),
      '',
      'Physical Activity:',
      '',
      peText,
      '',
      'Fine Arts:',
      '',
      ...(faCourses.length > 0
        ? faCourses.map(c => `  - ${c.name} (${c.code || 'no code'})`)
        : ['  (none selected)']),
      '',
      'Electives:',
      '',
      ...(electiveCourses.length > 0
        ? electiveCourses.map(c => `  ${c.name} (${c.code || 'no code'})`)
        : ['  (none selected)']),
      '',
      `Total courses: ${totalCourses}`,
    ].join('\n');
  }

  function handleCopy() {
    clearTimeout(shareDismissTimer.current);
    setShareTooltipVisible(false);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    const text = generatePlainText();
    const fallback = () => {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch { /* silent */ }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(fallback);
    } else {
      fallback();
    }
    setCopied(true);
    copyTimer.current = setTimeout(() => setCopied(false), 2000);
  }

  function handleClearConfirm() {
    clearSchedule();
    setCore([...DEFAULT_SCHEDULE.selections.core]);
    setPe(DEFAULT_SCHEDULE.selections.pe);
    setFineArts([...DEFAULT_SCHEDULE.selections.fineArts]);
    setElectives([...DEFAULT_SCHEDULE.selections.electives]);
    setLastSaved(null);
    setClearConfirm(false);
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="schedule-builder">
      <PageHero
        icon="/assets/eagle/page-icons/schedule.png"
        title="Build your schedule"
      />
      <p className="sb-subtitle">
        Pick your courses for 6th grade. Your choices save automatically — come back
        to update them anytime.
      </p>

      {/* ── View toggle ── */}
      <div className="sb-view-toggle" role="group" aria-label="Switch view">
        <button
          type="button"
          className={`sb-view-btn${view === 'schedule' ? ' sb-view-btn--active' : ''}`}
          onClick={() => setView('schedule')}
        >
          Schedule
        </button>
        <button
          type="button"
          className={`sb-view-btn${view === 'day' ? ' sb-view-btn--active' : ''}`}
          onClick={() => setView('day')}
        >
          See my day
        </button>
      </div>

      {/* ── Requirements bar (sticky) ── */}
      <div
        className="sb-requirements-bar"
        role="status"
        aria-live="polite"
        aria-label="Schedule requirements"
      >
        <RequirementCheck
          status="ok"
          label="4 core courses"
          detail="English, Math, Science, and Social Studies are all required. Swap to an advanced version using the links below."
        />
        <RequirementCheck
          status={physActivityOk ? 'ok' : 'warning'}
          label="1 physical activity"
          detail={
            physActivityOk
              ? 'Physical activity requirement met.'
              : 'Pick a PE course below — or select Dance MS 1 in Fine Arts.'
          }
        />
        <RequirementCheck
          status={fineArtsOk ? 'ok' : 'warning'}
          label={fineArts.length > 0 ? `${fineArts.length} fine art${fineArts.length !== 1 ? 's' : ''}` : '1 fine art'}
          detail={
            fineArtsOk
              ? 'Fine arts requirement met. You can add a second course below.'
              : 'Pick at least one fine arts course in the section below.'
          }
        />
        <RequirementCheck
          status={totalOk ? 'ok' : 'warning'}
          label={`${totalCourses} course${totalCourses !== 1 ? 's' : ''} total`}
          detail={
            totalOk
              ? 'Looks like a solid schedule.'
              : `Most 6th graders take 6–8 courses. You currently have ${totalCourses}.`
          }
        />
      </div>

      {/* ── Schedule view ── */}
      {view === 'schedule' && (
        <>
          {/* Core 4 */}
          <section className="sb-section">
            <h2 className="sb-section-title">Required core courses</h2>
            <p className="sb-section-desc">
              All four required for every 6th grader. Advanced versions are opt-in only.
            </p>
            <div className="sb-core-grid">
              {CORE_SLOTS.map((slot, index) => {
                const courseId   = core[index];
                const isAdvanced = courseId === slot.advancedId;
                let swapTarget = null, swapLabel = null, prereqWarning = null;

                if (slot.advancedId) {
                  if (!isAdvanced) {
                    swapTarget = slot.advancedId;
                    swapLabel  = slot.swapToAdvancedLabel;
                  } else {
                    swapTarget    = slot.defaultId;
                    swapLabel     = slot.swapToStandardLabel;
                    prereqWarning = slot.prereqWarning ?? null;
                  }
                }

                return (
                  <CoreCard
                    key={slot.defaultId}
                    courseId={courseId}
                    onSwap={id => handleCoreSwap(index, id)}
                    swapTarget={swapTarget}
                    swapLabel={swapLabel}
                    prereqWarning={prereqWarning}
                  />
                );
              })}
            </div>
          </section>

          {/* Physical activity */}
          <section className="sb-section">
            <h2 className="sb-section-title">Pick one physical activity</h2>
            <CoursePicker
              mode="single"
              options={PE_OPTIONS}
              selected={pe}
              onChange={setPe}
              dimmedIds={dimmedPeIds}
            />
            {pe === 'dance-cover-pe' && !danceIsFineArt && (
              <div className="sb-hint sb-hint--info">
                Select Dance MS 1 in Fine Arts below to satisfy your physical activity requirement.
              </div>
            )}
          </section>

          {/* Fine arts */}
          <section className="sb-section">
            <h2 className="sb-section-title">Pick your fine arts</h2>
            <p className="sb-section-desc">
              Required: at least 1. You may pick up to 2. Dance MS 1 counts as both fine arts and physical activity.
            </p>
            <CoursePicker
              mode="multi"
              max={2}
              options={FINE_ARTS_OPTIONS}
              selected={fineArts}
              onChange={handleFineArtsChange}
              dimmedIds={dimmedFaIds}
            />
            {danceIsFineArt && realPeSelected && (
              <div className="sb-hint sb-hint--info">
                Heads up: Dance already covers your physical activity. You can leave PE blank if you'd like.
              </div>
            )}
          </section>

          {/* Electives */}
          <section className="sb-section sb-section--last">
            <h2 className="sb-section-title">Pick your electives</h2>
            <p className="sb-section-desc">Most students take 1–3 electives.</p>
            <CoursePicker
              mode="multi"
              max={3}
              options={ELECTIVE_OPTIONS}
              selected={electives}
              onChange={setElectives}
            />
          </section>
        </>
      )}

      {/* ── Day view ── */}
      {view === 'day' && (
        <DayView
          core={core}
          pe={pe}
          fineArts={fineArts}
          electives={electives}
        />
      )}

      {/* ── Fixed action bar ── */}
      <div className="sb-action-bar">
        <div className="sb-action-buttons">
          <div className="sb-copy-wrapper">
            <div
              className={`sb-share-tooltip${shareTooltipVisible ? ' sb-share-tooltip--visible' : ''}`}
              aria-hidden="true"
            >
              Share with family and friends
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleCopy}
            >
              {copied ? 'Copied!' : 'Copy as text'}
            </button>
          </div>

          {clearConfirm ? (
            <div className="sb-clear-confirm">
              <span className="sb-clear-confirm-text">Are you sure? This can't be undone.</span>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleClearConfirm}
              >
                Yes, clear
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setClearConfirm(false)}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-ghost sb-clear-btn"
              onClick={() => setClearConfirm(true)}
            >
              Clear schedule
            </button>
          )}
        </div>

        <span className="sb-last-saved">
          {lastSaved ? `Saved ${relativeTime(lastSaved)}` : 'Not yet saved'}
        </span>
      </div>
    </div>
  );
}
